// Core design system tokens

// These are our standard colors
export const colors = {
  primary: {
    main: '#0D373D',
    hover: '#0A2A2F',
  },
  gray: {
    50: '#F5F8F9',
    100: '#E4E8EA',
    300: '#7A7D81',
    400: '#4D4F52',
    500: '#1D2125',
  },
  danger: {
    light: '#FFEAEA',
    hover: '#FFD6D6',
    text: '#D32F2F',
  },
  success: '#E2FDCB',
};

// Screen breakpoints
export const screens = {
  sm: '640px', // Mobile
  md: '768px', // Tablet
  lg: '1024px', // Small laptop
  xl: '1280px', // Desktop
  '2xl': '1536px', // Large screens
};

// Typography scale
export const typography = {
  size: {
    xs: 'text-xs', // 12px
    sm: 'text-sm', // 14px
    base: 'text-base', // 16px
    lg: 'text-lg', // 18px
    xl: 'text-xl', // 20px
    '2xl': 'text-2xl', // 24px
    '3xl': 'text-3xl', // 30px
  },
  weight: {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  },
  text: {
    // Headings
    h1: 'text-3xl',
    h2: 'text-xl md:text-2xl lg:text-3xl',
    h3: 'text-lg md:text-xl lg:text-2xl',
    h4: 'text-base sm:text-lg md:text-xl lg:text-xl font-medium',

    // Body text variations
    body: {
      large: 'text-base sm:text-lg lg:text-xl',
      default: 'text-sm sm:text-base lg:text-lg',
      small: 'text-xs sm:text-sm lg:text-base',
      emphasis: 'text-base font-medium',
    },

    // Special cases
    button: 'text-sm sm:text-base lg:text-lg',
    label: 'text-xs sm:text-sm font-medium',
    caption: 'text-xs text-gray-500',

    // New configuration text styles
    config: {
      label: 'text-xs sm:text-sm text-[#7A7D81]',
      value: 'text-sm sm:text-base text-[#1D2125]',
      title: 'text-base sm:text-lg text-[#4D4F52] font-medium',
      subtitle: 'text-xs sm:text-sm text-[#7A7D81]',
      button: 'text-xs sm:text-sm text-[#0D373D] hover:underline',
      description: 'text-sm sm:text-base text-[#7A7D81]',
    },
  },
} as const;

// Spacing scale
export const spacing = {
  0: '0px',
  1: '0.25rem', // 4px
  2: '0.5rem', // 8px
  3: '0.75rem', // 12px
  4: '1rem', // 16px
  6: '1.5rem', // 24px
  8: '2rem', // 32px
  12: '3rem', // 48px
};

// Border radius scale (static Tailwind classes)
export const radius = {
  none: 'rounded-none',
  sm: 'rounded', // 4px
  base: 'rounded-lg', // 8px
  xl: 'rounded-xl', // 12px
  '2xl': 'rounded-2xl', // 16px
  '3xl': 'rounded-3xl', // 24px
};

// Border width scale
export const border = {
  width: {
    none: '0',
    thin: '0.1rem',
    standard: '0.2rem',
    thick: '0.3rem',
  },
};

// Component size presets (static Tailwind classes)
export const size = {
  button: {
    sm: {
      padding: 'px-3 py-1.5',
      text: 'text-sm',
      icon: '14',
    },
    base: {
      padding: 'px-4 py-2',
      text: 'text-sm',
      icon: '16',
    },
    md: {
      padding: 'px-4 py-2',
      text: 'text-sm',
      icon: '18',
    },
    lg: 'px-5 py-2.5 text-base',
  },
  switch: {
    sm: {
      width: 'w-8 sm:w-10 lg:w-12',
      height: 'h-4 sm:h-5 lg:h-6',
      thumb: 'h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5',
      translate: 'translate-x-4 sm:translate-x-5 lg:translate-x-6',
    },
    base: {
      width: 'w-10 sm:w-12 lg:w-14',
      height: 'h-5 sm:h-6 lg:h-7',
      thumb: 'h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6',
      translate: 'translate-x-5 sm:translate-x-6 lg:translate-x-7',
    },
    lg: {
      width: 'w-12 sm:w-14 lg:w-16',
      height: 'h-6 sm:h-7 lg:h-8',
      thumb: 'h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7',
      translate: 'translate-x-6 sm:translate-x-7 lg:translate-x-8',
    },
  },
  input: {
    sm: {
      height: 'py-1.5 sm:py-2',
      text: 'text-xs sm:text-sm',
      padding: 'px-2 sm:px-3',
      icon: '14 sm:16',
      placeholder: 'placeholder:text-xs placeholder:sm:text-sm',
    },
    base: {
      height: 'py-2 sm:py-2.5',
      text: 'text-sm sm:text-base',
      padding: 'px-3 sm:px-4',
      icon: '16 sm:18',
      placeholder: 'placeholder:text-base',
    },
    lg: {
      height: 'py-2.5 sm:py-3',
      text: 'text-base sm:text-lg',
      padding: 'px-4 sm:px-5',
      icon: '18 sm:20',
      placeholder: 'placeholder:text-base placeholder:sm:text-lg',
    },
    xl: {
      height: 'py-3 sm:py-3.5',
      text: 'text-lg sm:text-xl',
      padding: 'px-5 sm:px-6',
      icon: '20 sm:24',
      placeholder: 'placeholder:text-lg placeholder:sm:text-xl',
    },
  },
  icon: {
    xs: 14,
    sm: 16,
    base: 20,
    lg: 24,
    xl: 32,
  },
  card: {
    none: '',
    sm: 'p-4',
    base: 'p-6',
    lg: 'p-8',
  },
};

// Z-index scale
export const zIndex = {
  dropdown: 50,
  sticky: 100,
  modal: 200,
  popover: 300,
  tooltip: 400,
};
